/**
 * Per-tab, memory-only state of the Deadlock Analyzer: the parsed report and
 * the parse status. Nothing here is persisted — the tab content keeps source
 * metadata only (see `DeadlockAnalyzerTabContent`).
 */
import { create } from 'zustand';
import type { DeadlockParseError, DeadlockReport } from '@/core/deadlocks';

export type DeadlockParseStatus =
  | { status: 'idle' }
  | { status: 'parsing' }
  | { status: 'ready'; report: DeadlockReport }
  | { status: 'error'; error: DeadlockParseError };

type DeadlockAnalyzerState = {
  byTab: Record<string, DeadlockParseStatus>;
  setStatus: (tabId: string, status: DeadlockParseStatus) => void;
  clear: (tabId: string) => void;
};

export const useDeadlockAnalyzerStore = create<DeadlockAnalyzerState>((set) => ({
  byTab: {},
  setStatus: (tabId, status) => set((state) => ({ byTab: { ...state.byTab, [tabId]: status } })),
  clear: (tabId) =>
    set((state) => {
      const next = { ...state.byTab };
      delete next[tabId];
      return { byTab: next };
    }),
}));

const IDLE: DeadlockParseStatus = { status: 'idle' };

export function useDeadlockParseStatus(tabId: string): DeadlockParseStatus {
  return useDeadlockAnalyzerStore((s) => s.byTab[tabId] ?? IDLE);
}

/** Called by `workspaceStore.removeTab` so a closed tab does not keep its report XML alive. */
export function releaseDeadlockAnalyzerTab(tabId: string): void {
  useDeadlockAnalyzerStore.getState().clear(tabId);
}
